import React, { useCallback, useState } from 'react';

import { cn } from 'components/lib/utils';
import Typography from 'components/Typography/Typography';

import { useResendCountdown } from './use-resend-countdown.hook';

interface ResendSectionProps {
  onResend: () => Promise<void>;
  cooldownEndsAtMs: number | null;
  disabled?: boolean;
  className?: string;
}

const buildResendCountdownLabel = (remainingSeconds: number): string =>
  `Resend code (${remainingSeconds}s)`;

const getResendDisabledLabel = (isResending: boolean, remainingSeconds: number): string => {
  if (isResending) {
    return 'Resending...';
  }
  if (remainingSeconds > 0) {
    return buildResendCountdownLabel(remainingSeconds);
  }
  return 'Resend code';
};

export const ResendSection: React.FC<ResendSectionProps> = ({
  onResend,
  cooldownEndsAtMs,
  disabled = false,
  className
}) => {
  const remainingSeconds = useResendCountdown(cooldownEndsAtMs);
  const [isResending, setIsResending] = useState(false);

  const handleResend = useCallback(async () => {
    setIsResending(true);
    try {
      await onResend();
    } finally {
      setIsResending(false);
    }
  }, [onResend]);

  const isResendDisabled = disabled || remainingSeconds > 0 || isResending;
  const disabledLabel = getResendDisabledLabel(isResending, remainingSeconds);

  if (isResendDisabled) {
    return (
      <div className={cn('text-center', className)}>
        <Typography className="text-p-s text-base-mutedForeground">
          {disabledLabel}
        </Typography>
      </div>
    );
  }

  return (
    <div className={cn('text-center', className)}>
      <Typography
        className="text-p-s text-base-primary cursor-pointer underline"
        onClick={handleResend}
      >
        Resend code
      </Typography>
    </div>
  );
};
